const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;

/**
 * Vertical FOV (degrees) for the render camera so 3D objects stay angularly locked to a
 * cover-fitted camera feed. The feed is displayed `object-fit: cover` (cropped to fill the screen),
 * so the render camera must reproduce the FOV of the *visible* (post-crop) region — otherwise the
 * 3D moves at a different angular rate than the world in the feed and slides as the user pans.
 *
 * `cameraHFovDeg` is the device camera's horizontal FOV in its native frame (a per-device-ish
 * constant; tune on target hardware). `videoAspect` = native videoWidth/videoHeight,
 * `screenAspect` = viewport width/height.
 */
export function coverFitCameraVerticalFovDeg(
  videoAspect: number,
  screenAspect: number,
  cameraHFovDeg: number,
): number {
  const halfH = (cameraHFovDeg * DEG2RAD) / 2;

  // Screen at least as tall (relative to width) as the video → cover-fit crops the SIDES; the full
  // sensor height stays visible, so the vertical FOV is the native vertical FOV.
  if (screenAspect <= videoAspect) {
    return 2 * Math.atan(Math.tan(halfH) / videoAspect) * RAD2DEG;
  }

  // Screen wider than the video → cover-fit crops TOP/BOTTOM; the full sensor width stays visible,
  // so the visible vertical FOV follows from the horizontal FOV and the screen aspect.
  return 2 * Math.atan(Math.tan(halfH) / screenAspect) * RAD2DEG;
}
